import { useState } from 'react';
import type { RuleSet } from '@/game/rules';
import type { EntityId, PlayerId, SimState } from '@/game/sim';
import { chevrons } from '@/game/view/activity';
import type { Names } from '@/game/view/names';
import { type SendTarget, type TroopRow, troopOptions } from '@/game/view/sendTroops';
import { DockPanel } from './hud/DockPanel';

interface SendTroopsPanelProps {
  state: SimState;
  playerId: PlayerId;
  rules: RuleSet;
  cityNumbers: ReadonlyMap<string, number>;
  /** The player's own names for groups and cities (client-local). */
  names?: Names;
  target: SendTarget;
  onSend: (unitIds: EntityId[]) => void;
  onFocusTarget: () => void;
  onClose: () => void;
}

/**
 * Send troops picker (bottom-centre dock), opened from a notification: tick
 * groups, idle military by city and barracks/factory stores, then Send gives them an
 * attack order on the target (stored units come out first).
 */
export function SendTroopsPanel({
  state,
  playerId,
  rules,
  cityNumbers,
  names,
  target,
  onSend,
  onFocusTarget,
  onClose,
}: SendTroopsPanelProps) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const options = troopOptions(state, rules, playerId, cityNumbers, names);
  const rows = [...options.groups, ...options.idle, ...options.stored];
  const chosen = rows.filter((r) => picked.has(r.key));
  const unitIds = [...new Set(chosen.flatMap((r) => r.unitIds))];

  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const section = (title: string, list: TroopRow[]) =>
    list.length > 0 && (
      <div className="flex flex-col gap-0.5">
        <p className="px-1 text-[11px] text-fg-subtle">{title}</p>
        {list.map((r) => (
          <label
            key={r.key}
            className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 transition ${picked.has(r.key) ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`}
          >
            <input
              type="checkbox"
              checked={picked.has(r.key)}
              onChange={() => toggle(r.key)}
              className="accent-accent"
            />
            <span className="font-medium">{r.label}</span>
            {r.rank > 0 && (
              <span title={`Highest rank ${r.rank}`} className="text-[8px] leading-none text-warn">
                {chevrons(r.rank)}
              </span>
            )}
            <span className="flex-1 truncate text-right text-fg-muted">{r.detail}</span>
          </label>
        ))}
      </div>
    );

  return (
    <DockPanel
      kind="send troops"
      title="Send troops"
      chips={
        <button
          type="button"
          title="Show the target"
          onClick={onFocusTarget}
          className="chip min-w-0 cursor-pointer hover:bg-fill-hover"
        >
          <span className="truncate">⚔️ {target.label}</span> ⌖
        </button>
      }
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <span className="flex-1 px-1 text-fg-muted">
            {unitIds.length > 0
              ? `${unitIds.length} ${unitIds.length === 1 ? 'unit' : 'units'} picked`
              : 'Pick troops to send'}
          </span>
          <button
            type="button"
            disabled={unitIds.length === 0}
            onClick={() => onSend(unitIds)}
            className="btn btn-primary h-7 px-4 text-xs"
          >
            Send
          </button>
        </div>
      }
    >
      {rows.length === 0 ? (
        <p className="px-1 text-fg-muted">
          No military units: build a barracks and train some from its city
        </p>
      ) : (
        <>
          {section('Groups', options.groups)}
          {section('Idle', options.idle)}
          {section('Stored', options.stored)}
        </>
      )}
    </DockPanel>
  );
}
